import React from "react"
import { Link } from "react-router-dom"
import {
  ArrowRight,
  BookOpen,
  CalendarCheck,
  Check,
  Clock,
  Compass,
  Info,
  MessageSquare,
  Plug,
  UserCheck,
  Users,
} from "lucide-react"
import SectionHeading from "../home/SectionHeading.jsx"
import { CONTACT_QUOTE } from "../../data/services.js"

const ICONS = { BookOpen, CalendarCheck, Clock, Compass, MessageSquare, Plug, UserCheck, Users }

// Highlighted capability on a service page (`page.feature` in data/services.js),
// with its own anchor so other pages can link straight to it.
export default function FeatureSection({
  id,
  eyebrow,
  heading,
  lead,
  capabilitiesHeading,
  capabilities,
  benefitsHeading,
  benefits,
  note,
  cta,
}) {
  const headingId = `${id}-heading`

  return (
    <section
      id={id}
      aria-labelledby={headingId}
      className="scroll-mt-24 bg-gradient-to-b from-[#eef5ff] to-white py-16 sm:py-24"
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="grid gap-10 lg:grid-cols-[1.2fr_1fr] lg:items-start">
          <div>
            <SectionHeading id={headingId} eyebrow={eyebrow} title={heading} lead={lead} />

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Link
                to={cta.to}
                className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full bg-brand-primary px-7 py-3 text-base font-semibold text-white shadow-button transition hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"
              >
                {cta.label}
                <ArrowRight aria-hidden="true" className="h-4 w-4" />
              </Link>
              <Link
                to={`${CONTACT_QUOTE}&service=chatbot`}
                className="inline-flex min-h-[48px] items-center justify-center rounded-full border border-brand-heading/20 px-7 py-3 text-base font-semibold text-brand-heading transition hover:bg-brand-primarySoft focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"
              >
                Request a Quote
              </Link>
            </div>
          </div>

          <aside
            aria-labelledby={`${id}-benefits-heading`}
            className="rounded-xl2 bg-brand-navy p-7 text-white shadow-cardStrong sm:p-8"
          >
            <h3 id={`${id}-benefits-heading`} className="text-xl font-semibold">
              {benefitsHeading}
            </h3>
            <ul className="mt-5 space-y-3">
              {benefits.map((benefit) => (
                <li key={benefit} className="flex gap-3 text-blue-50">
                  <Check aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-brand-sky" />
                  {benefit}
                </li>
              ))}
            </ul>
          </aside>
        </div>

        <div className="mt-12 rounded-xl2 border border-brand-border bg-white p-6 shadow-card sm:p-8">
          <h3 className="text-xl font-semibold text-brand-heading">{capabilitiesHeading}</h3>
          <ul className="mt-6 grid gap-x-8 gap-y-7 sm:grid-cols-2 lg:grid-cols-4">
            {capabilities.map((item) => {
              const Icon = ICONS[item.icon]

              return (
                <li key={item.title}>
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-primarySoft text-brand-primary">
                    {Icon && <Icon aria-hidden="true" className="h-5 w-5" />}
                  </span>
                  <h4 className="mt-3 font-semibold text-brand-heading">{item.title}</h4>
                  <p className="mt-1.5 text-sm leading-relaxed text-brand-textSoft">{item.text}</p>
                </li>
              )
            })}
          </ul>
        </div>

        {note && (
          <p className="mt-6 flex max-w-3xl gap-3 rounded-xl2 border border-brand-border bg-white p-5 text-sm leading-relaxed text-brand-textSoft">
            <Info aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-brand-primary" />
            {note}
          </p>
        )}
      </div>
    </section>
  )
}
